import { FoodCategory } from '@/types/nutrition'

/**
 * Food categorization utility for assigning categories to food items based on their names.
 *
 * Uses a comprehensive multi-strategy parsing algorithm that handles:
 * - Exact matches from comprehensive food database
 * - Brand prefix normalization (e.g., "Brand - Product" -> "Product")
 * - Fuzzy matching for common food variations
 * - Keyword-based fallback matching
 * - Type-safe error handling and validation
 */

// Enhanced food categorization strategies
type CategorizationStrategy = 'exact' | 'brand-normalized' | 'fuzzy' | 'keyword' | 'unknown'

interface CategorizationResult {
  readonly category: FoodCategory
  readonly strategy: CategorizationStrategy
  readonly confidence: number // 0-1 scale
  readonly normalizedName: string
  readonly matchedTerm?: string
}

// Comprehensive mapping of food names to categories based on food_categories.md
const foodCategoryMapping: Record<string, FoodCategory> = {
  // Fruits and Vegetables
  apple: 'Fruits and Vegetables',
  banana: 'Fruits and Vegetables',
  broccoli: 'Fruits and Vegetables',
  'brussels sprouts': 'Fruits and Vegetables',
  cabbage: 'Fruits and Vegetables',
  'cabbage, red, raw': 'Fruits and Vegetables',
  cauliflower: 'Fruits and Vegetables',
  cherries: 'Fruits and Vegetables',
  corn: 'Fruits and Vegetables',
  'corn canned': 'Fruits and Vegetables',
  cucumber: 'Fruits and Vegetables',
  'frozen cherries': 'Fruits and Vegetables',
  grapefruit: 'Fruits and Vegetables',
  'green bell pepper': 'Fruits and Vegetables',
  'iceberg lettuce': 'Fruits and Vegetables',
  kiwi: 'Fruits and Vegetables',
  mandarin: 'Fruits and Vegetables',
  mango: 'Fruits and Vegetables',
  'napa cabbage': 'Fruits and Vegetables',
  nectarine: 'Fruits and Vegetables',
  'olives - pickled, canned or bottled, green': 'Fruits and Vegetables',
  onion: 'Fruits and Vegetables',
  orange: 'Fruits and Vegetables',
  peach: 'Fruits and Vegetables',
  pear: 'Fruits and Vegetables',
  plum: 'Fruits and Vegetables',
  'sauerkraut (fermented cabbage)': 'Fruits and Vegetables',
  'seaweed, wakame, raw': 'Fruits and Vegetables',
  'spinach, raw, fresh': 'Fruits and Vegetables',
  tomato: 'Fruits and Vegetables',
  'tomato juice, 100%': 'Fruits and Vegetables',
  'yellow bell pepper': 'Fruits and Vegetables',
  'natural garden - healthy seaweed snacks - kimchi flavor': 'Fruits and Vegetables',
  'гринвиль - морковка по-корейски (korean-style carrots)': 'Fruits and Vegetables',
  'премия - манго сушене (dried mango)': 'Fruits and Vegetables',
  'премія - чука (chuka seaweed salad)': 'Fruits and Vegetables',
  'премия - черника с сахаром (blueberries with sugar)': 'Fruits and Vegetables',

  // Legumes
  'hummus, plain (made from chickpeas)': 'Legumes',
  'lentils, dry': 'Legumes',

  // Whole Grains
  'barley, hulled': 'Whole Grains',
  'buckwheat groats, roasted, dry': 'Whole Grains',
  'finn crisp - caraway (rye crackers)': 'Whole Grains',
  popcorn: 'Whole Grains',
  'whole wheat flour, all-purpose': 'Whole Grains',
  'премія - попкорн со слив. маслом (popcorn with butter)': 'Whole Grains',
  'росток - хлібці хрусткі з морською сіллю (crispy crackers with sea salt)': 'Whole Grains',
  "рум'янець - хліб цільнозерновий (whole grain bread)": 'Whole Grains',

  // Nuts and Seeds - Enhanced with missing items
  almonds: 'Nuts and Seeds',
  'cacao powder': 'Nuts and Seeds',
  'chia seeds': 'Nuts and Seeds',
  'flax seeds': 'Nuts and Seeds',
  hazelnuts: 'Nuts and Seeds',
  'usda - hazelnuts': 'Nuts and Seeds', // Brand-normalized match for "Sources-usda - Hazelnuts"
  'pumpkin seeds': 'Nuts and Seeds',
  'safeway - pistachios': 'Nuts and Seeds',
  'sesame seeds': 'Nuts and Seeds',

  // Root Vegetables
  'beets - cooked, boiled, drained': 'Root Vegetables',
  carrots: 'Root Vegetables',
  'generic (usda) - beetroot, raw': 'Root Vegetables',
  potato: 'Root Vegetables',

  // Herbs and Spices
  'dijon mustard': 'Herbs and Spices',
  'dill weed, fresh': 'Herbs and Spices',
  'ground ginger': 'Herbs and Spices',
  'parsley, dried': 'Herbs and Spices',

  // Meat, Poultry, Fish & Seafood
  '93% lean beef - beef': 'Meat, Poultry, Fish & Seafood',
  'canned tuna': 'Meat, Poultry, Fish & Seafood',
  caviar: 'Meat, Poultry, Fish & Seafood',
  'chicken drumstick': 'Meat, Poultry, Fish & Seafood',
  'chicken breast, raw, skinless': 'Meat, Poultry, Fish & Seafood',
  'chicken thigh, raw, with skin': 'Meat, Poultry, Fish & Seafood',
  'ctrlaltdel - atlantic herring': 'Meat, Poultry, Fish & Seafood',
  'fish, mackerel, atlantic, raw': 'Meat, Poultry, Fish & Seafood',
  'fish, salmon, chinook, cooked, dry heat': 'Meat, Poultry, Fish & Seafood',
  'five oceans - smoked wild pink salmon': 'Meat, Poultry, Fish & Seafood',
  'foodhold usa - sirloin tip steak': 'Meat, Poultry, Fish & Seafood',
  ham: 'Meat, Poultry, Fish & Seafood',
  'publix - boneless skinless chicken thighs': 'Meat, Poultry, Fish & Seafood',
  'publix - chicken hearts': 'Meat, Poultry, Fish & Seafood',
  'raw turkey breast': 'Meat, Poultry, Fish & Seafood',
  'sams salmon - salmon': 'Meat, Poultry, Fish & Seafood',
  shrimp: 'Meat, Poultry, Fish & Seafood',
  'tilapia raw': 'Meat, Poultry, Fish & Seafood',
  'бащинський - филе куриное карчено-вареное (smoked-boiled chicken fillet)':
    'Meat, Poultry, Fish & Seafood',

  // Dairy & Eggs
  'activia - yogurt, lowfat, 1.5% milkfat, vanilla': 'Dairy & Eggs',
  'astro - 2% plain yogurt': 'Dairy & Eggs',
  'cottage tnuva - cottage 5%': 'Dairy & Eggs',
  'cream 10% fat': 'Dairy & Eggs',
  'cream 15% fat': 'Dairy & Eggs',
  'ctrlaltdel - kefir 1%': 'Dairy & Eggs',
  'cub milk - milk - 1%': 'Dairy & Eggs',
  'dairyland - canzona danablu danish (blue cheese)': 'Dairy & Eggs',
  eggs: 'Dairy & Eggs',
  'feta cheese': 'Dairy & Eggs',
  'ghidetti - grana padano (cheese)': 'Dairy & Eggs',
  'greek yogurt, plain, nonfat, 0%': 'Dairy & Eggs',
  'iga 2% milk - 2% milk': 'Dairy & Eggs',
  'just ours - cottage cheese (non-fat)': 'Dairy & Eggs',
  'mozzarella cheese, whole milk': 'Dairy & Eggs',
  'the cheese guy - fresh mozarella cheese': 'Dairy & Eggs',
  'veldhuyzen kaas hommage - сир ніжний козячий 50% (gentle goat cheese 50%)': 'Dairy & Eggs',
  'weis - nonfat milk': 'Dairy & Eggs',
  'галичина - йогурт без цукру 2, 2% (yogurt without sugar 2.2%)': 'Dairy & Eggs',
  'сыр фета - фета (feta cheese)': 'Dairy & Eggs',
  'lekker - йогурт з фісташкою та кокосом (yogurt with pistachio and coconut)': 'Dairy & Eggs',

  // Fats, Oils & Spreads
  "barilla - pesto senz'aglio": 'Fats, Oils & Spreads',
  'butter, unsalted': 'Fats, Oils & Spreads',
  'coconut oil': 'Fats, Oils & Spreads',
  'oil, sunflower': 'Fats, Oils & Spreads',
  'olive oil': 'Fats, Oils & Spreads',
  'peanut butter': 'Fats, Oils & Spreads',

  // Processed Foods & Snacks
  'aviko - placki (potato pancakes)': 'Processed Foods & Snacks',
  'ctrlaltdel - french fries': 'Processed Foods & Snacks',
  'fresh gourmet - crispy onions': 'Processed Foods & Snacks',
  'pasta, dry (assumed white pasta)': 'Processed Foods & Snacks',
  'pizza pizza - small cheese pizza': 'Processed Foods & Snacks',
  'rice noodles, dry': 'Processed Foods & Snacks',
  'schulstad - butter croissants': 'Processed Foods & Snacks',
  'white bread': 'Processed Foods & Snacks',
  'flint craft grenki - грінки житньо-пшеничні хвилясті зі смаком часник (rye-wheat wavy croutons with garlic flavor)':
    'Processed Foods & Snacks',
  'lekorna - вафельні коржі для торта з какао (waffle layers for cake with cacao)':
    'Processed Foods & Snacks',
  'ведмедик барні - тістечко бісквітне з шоколадною начинкою (barny bear - sponge cake with chocolate filling)':
    'Processed Foods & Snacks',
  'левада - млинці з солодким сиром (crepes with sweet cheese)': 'Processed Foods & Snacks',
  'печиво вівсяне - печиво вівсяне (oatmeal cookies)': 'Processed Foods & Snacks',
  'три ведмеді - пельмені мішутка (курча) (three bears - mishutka pelmeni/dumplings with chicken)':
    'Processed Foods & Snacks',

  // Sweets, Sugars & Desserts
  'haagen dazs salted caramel ice cream': 'Sweets, Sugars & Desserts',
  'kinder - chocolate': 'Sweets, Sugars & Desserts',
  maltodextrin: 'Sweets, Sugars & Desserts',
  marshmallows: 'Sweets, Sugars & Desserts',
  'now real food - fructose': 'Sweets, Sugars & Desserts',
  "smucker's - cherry jam": 'Sweets, Sugars & Desserts',
  sugar: 'Sweets, Sugars & Desserts',
  'sweetened condensed milk': 'Sweets, Sugars & Desserts',
  'green leaf - молоко згущене з солодкою стевією (condensed milk with sweet stevia)':
    'Sweets, Sugars & Desserts',
  'lifesavers - gummies': 'Sweets, Sugars & Desserts',
  'millennium - dark chocolate with stevia': 'Sweets, Sugars & Desserts',
  'milka - молочний шоколад (milk chocolate)': 'Sweets, Sugars & Desserts',
  'миллениум - молочная шоколадка на стевии (millennium - milk chocolate with stevia)':
    'Sweets, Sugars & Desserts',
  'tom - солона карамель з полуницею (salted caramel with strawberry)': 'Sweets, Sugars & Desserts',
  'волошкове поле пломбир - пломбир мороженое (plombir ice cream)': 'Sweets, Sugars & Desserts',

  // Supplements & Protein Powders
  'biotech - 100% whey protein': 'Supplements & Protein Powders',
  'lipobar - protein bar': 'Supplements & Protein Powders',
  'myprotein - micellar casein': 'Supplements & Protein Powders',
  'myprotein - omega 3': 'Supplements & Protein Powders',
  'olimp - gain bolic 6000': 'Supplements & Protein Powders',
  'ostrovit - eea': 'Supplements & Protein Powders',
  'psilium - psilium (psyllium)': 'Supplements & Protein Powders',

  // Beverages
  'red bull - red bull': 'Beverages',
  'spraga - комбуча малина та шипшина (kombucha raspberry and rosehip)': 'Beverages',
}

/**
 * Normalizes a food name for matching by converting to lowercase,
 * removing extra spaces, and handling common variations.
 *
 * @param name - Raw food name
 * @returns Normalized food name for consistent matching
 */
function normalizeFoodName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/['']/g, "'") // Normalize apostrophes
    .replace(/[.,;:()[\]{}]/g, '') // Remove common punctuation
    .replace(/\s*-\s*/g, ' - ') // Normalize dashes with spaces
}

/**
 * Enhanced fuzzy matching utility for handling food name variations.
 * Implements Levenshtein distance for similarity scoring.
 */
const FuzzyMatcher = {
  SIMILARITY_THRESHOLD: 0.8,

  /**
   * Calculates Levenshtein distance between two strings
   */
  levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = Array.from({ length: str2.length + 1 }, (_, i) =>
      Array.from({ length: str1.length + 1 }, (_, j) => (i === 0 ? j : 0))
    )

    // Initialize first column
    for (let i = 1; i <= str2.length; i++) {
      const row = matrix[i]
      if (row) {
        row[0] = i
      }
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        const cost = str2.charAt(i - 1) === str1.charAt(j - 1) ? 0 : 1
        const currentRow = matrix[i]
        const prevRow = matrix[i - 1]

        if (currentRow && prevRow) {
          currentRow[j] = Math.min(
            (prevRow[j] ?? 0) + 1, // deletion
            (currentRow[j - 1] ?? 0) + 1, // insertion
            (prevRow[j - 1] ?? 0) + cost // substitution
          )
        }
      }
    }

    const lastRow = matrix[str2.length]
    return lastRow ? (lastRow[str1.length] ?? 0) : 0
  },

  /**
   * Calculates similarity score between two strings (0-1)
   */
  calculateSimilarity(str1: string, str2: string): number {
    const maxLength = Math.max(str1.length, str2.length)
    if (maxLength === 0) return 1

    const distance = this.levenshteinDistance(str1, str2)
    return 1 - distance / maxLength
  },

  /**
   * Finds the best fuzzy match from a list of candidates
   */
  findBestMatch(
    target: string,
    candidates: readonly string[]
  ): { match: string; similarity: number } | null {
    let bestMatch: string | null = null
    let bestSimilarity = 0

    for (const candidate of candidates) {
      const similarity = this.calculateSimilarity(target, candidate)
      if (similarity > bestSimilarity && similarity >= this.SIMILARITY_THRESHOLD) {
        bestMatch = candidate
        bestSimilarity = similarity
      }
    }

    return bestMatch ? { match: bestMatch, similarity: bestSimilarity } : null
  },
} as const

/**
 * Enhanced categorization engine with multiple parsing strategies
 */
class FoodCategorizationEngine {
  private readonly foodMappingKeys = Object.keys(foodCategoryMapping) as readonly string[]

  /**
   * Strategy 1: Exact match lookup
   */
  private exactMatch(normalizedName: string): CategorizationResult | null {
    const category = foodCategoryMapping[normalizedName]
    if (!category) return null

    return {
      category,
      strategy: 'exact',
      confidence: 1.0,
      normalizedName,
      matchedTerm: normalizedName,
    }
  }

  /**
   * Strategy 2: Brand prefix removal and retry
   * Handles patterns like "Brand Name - Product Name"
   */
  private brandNormalizedMatch(normalizedName: string): CategorizationResult | null {
    // Remove brand prefix pattern: "brand - product" -> "product"
    const withoutBrand = normalizedName.replace(/^[^-]+ - /, '')
    if (withoutBrand === normalizedName) return null

    const category = foodCategoryMapping[withoutBrand]
    if (!category) return null

    return {
      category,
      strategy: 'brand-normalized',
      confidence: 0.9,
      normalizedName,
      matchedTerm: withoutBrand,
    }
  }

  /**
   * Strategy 3: Fuzzy matching for typos and variations
   */
  private fuzzyMatch(normalizedName: string): CategorizationResult | null {
    const fuzzyResult = FuzzyMatcher.findBestMatch(normalizedName, this.foodMappingKeys)
    if (!fuzzyResult) return null

    const category = foodCategoryMapping[fuzzyResult.match]
    if (!category) return null

    return {
      category,
      strategy: 'fuzzy',
      confidence: fuzzyResult.similarity,
      normalizedName,
      matchedTerm: fuzzyResult.match,
    }
  }

  /**
   * Strategy 4: Keyword-based fallback matching
   * Enhanced with more comprehensive patterns
   */
  private keywordMatch(normalizedName: string): CategorizationResult | null {
    // Order matters - more specific matches should come first
    const keywordPatterns: { pattern: RegExp; category: FoodCategory; confidence: number }[] = [
      // Fruits and Vegetables - Enhanced patterns
      {
        pattern: /\b(apples?|bananas?|oranges?|grapes?|berrys?|berries|fruits?)\b/i,
        category: 'Fruits and Vegetables',
        confidence: 0.8,
      },
      {
        pattern: /\b(mangos?|mangoes|pineapples?|kiwis?|peaches?|pears?)\b/i,
        category: 'Fruits and Vegetables',
        confidence: 0.8,
      },
      {
        pattern: /\b(broccoli|spinach|lettuce|cabbage|vegetables?)\b/i,
        category: 'Fruits and Vegetables',
        confidence: 0.8,
      },
      {
        pattern: /\b(tomatos?|tomatoes|onions?|peppers?|corn|peas?)\b/i,
        category: 'Fruits and Vegetables',
        confidence: 0.8,
      },

      // Nuts and Seeds - Enhanced with missing patterns
      {
        pattern: /\b(almonds?|walnuts?|pecans?|cashews?|pistachios?)\b/i,
        category: 'Nuts and Seeds',
        confidence: 0.8,
      },
      {
        pattern: /\b(hazelnuts?|macadamias?|brazil nuts?)\b/i,
        category: 'Nuts and Seeds',
        confidence: 0.8,
      },
      {
        pattern: /\b(sesame|sunflower|pumpkin|chia|flax)\s*(seeds?)\b/i,
        category: 'Nuts and Seeds',
        confidence: 0.8,
      },
      { pattern: /\b(seeds?|nuts?)\b/i, category: 'Nuts and Seeds', confidence: 0.6 },

      // Whole Grains
      { pattern: /\b(oat|oats|barley|quinoa|grain)\b/i, category: 'Whole Grains', confidence: 0.8 },
      { pattern: /\b(wheat|rice|rye|buckwheat)\b/i, category: 'Whole Grains', confidence: 0.7 },

      // Legumes
      { pattern: /\b(lentil|chickpea|bean|pea|hummus)\b/i, category: 'Legumes', confidence: 0.8 },

      // Root Vegetables
      {
        pattern: /\b(potato|carrot|beet|radish|turnip)\b/i,
        category: 'Root Vegetables',
        confidence: 0.8,
      },

      // Other categories (existing patterns)
      {
        pattern: /\b(ice cream|chocolate|sugar|candy)\b/i,
        category: 'Sweets, Sugars & Desserts',
        confidence: 0.8,
      },
      {
        pattern: /\b(protein|whey|casein|supplement)\b/i,
        category: 'Supplements & Protein Powders',
        confidence: 0.8,
      },
      { pattern: /\b(milk|cheese|yogurt|cream|egg)\b/i, category: 'Dairy & Eggs', confidence: 0.8 },
      {
        pattern: /\b(chicken|beef|fish|salmon|tuna|meat)\b/i,
        category: 'Meat, Poultry, Fish & Seafood',
        confidence: 0.8,
      },
      { pattern: /\b(oil|butter|margarine)\b/i, category: 'Fats, Oils & Spreads', confidence: 0.8 },
      {
        pattern: /\b(bread|pasta|pizza|cracker)\b/i,
        category: 'Processed Foods & Snacks',
        confidence: 0.7,
      },
    ]

    for (const { pattern, category, confidence } of keywordPatterns) {
      if (pattern.test(normalizedName)) {
        return {
          category,
          strategy: 'keyword',
          confidence,
          normalizedName,
          matchedTerm: pattern.source,
        }
      }
    }

    return null
  }

  /**
   * Main categorization method that tries all strategies in order
   */
  categorize(foodName: string): CategorizationResult {
    // Input validation
    if (!foodName || typeof foodName !== 'string' || foodName.trim() === '') {
      return {
        category: 'Unknown',
        strategy: 'unknown',
        confidence: 0,
        normalizedName: '',
        matchedTerm: 'invalid-input',
      }
    }

    const normalizedName = normalizeFoodName(foodName)

    // Try strategies in order of confidence
    const strategies = [
      () => this.exactMatch(normalizedName),
      () => this.brandNormalizedMatch(normalizedName),
      () => this.fuzzyMatch(normalizedName),
      () => this.keywordMatch(normalizedName),
    ]

    for (const strategy of strategies) {
      const result = strategy()
      if (result) return result
    }

    // Fallback to unknown
    return {
      category: 'Unknown',
      strategy: 'unknown',
      confidence: 0,
      normalizedName,
      matchedTerm: 'no-match',
    }
  }
}

// Create singleton instance
const categorizationEngine = new FoodCategorizationEngine()

/**
 * Attempts to find a category match using various strategies:
 * 1. Exact match
 * 2. Match without brand prefix (e.g., "Brand - Product Name" -> "Product Name")
 * 3. Fuzzy matching for typos and variations
 * 4. Keyword-based matching for common food terms
 *
 * @deprecated Use the new categorizeFood function instead
 */
function _findCategoryMatch(normalizedName: string): FoodCategory | null {
  // Strategy 1: Exact match
  if (foodCategoryMapping[normalizedName]) {
    return foodCategoryMapping[normalizedName]
  }

  // Strategy 2: Try without brand prefix (remove "Brand - " pattern)
  const withoutBrand = normalizedName.replace(/^[^-]+ - /, '')
  if (withoutBrand !== normalizedName && foodCategoryMapping[withoutBrand]) {
    return foodCategoryMapping[withoutBrand]
  }

  // Strategy 3: Keyword-based matching for common terms
  // Order matters - more specific matches should come first
  const keywordMatches: Record<string, FoodCategory> = {
    'ice cream': 'Sweets, Sugars & Desserts',
    chocolate: 'Sweets, Sugars & Desserts',
    sugar: 'Sweets, Sugars & Desserts',
    protein: 'Supplements & Protein Powders',
    whey: 'Supplements & Protein Powders',
    casein: 'Supplements & Protein Powders',
    milk: 'Dairy & Eggs',
    cheese: 'Dairy & Eggs',
    yogurt: 'Dairy & Eggs',
    cottage: 'Dairy & Eggs',
    kefir: 'Dairy & Eggs',
    cream: 'Dairy & Eggs',
    egg: 'Dairy & Eggs',
    chicken: 'Meat, Poultry, Fish & Seafood',
    beef: 'Meat, Poultry, Fish & Seafood',
    turkey: 'Meat, Poultry, Fish & Seafood',
    salmon: 'Meat, Poultry, Fish & Seafood',
    fish: 'Meat, Poultry, Fish & Seafood',
    tuna: 'Meat, Poultry, Fish & Seafood',
    shrimp: 'Meat, Poultry, Fish & Seafood',
    bread: 'Processed Foods & Snacks',
    pasta: 'Processed Foods & Snacks',
    pizza: 'Processed Foods & Snacks',
    oil: 'Fats, Oils & Spreads',
    butter: 'Fats, Oils & Spreads',
  }

  for (const [keyword, category] of Object.entries(keywordMatches)) {
    if (normalizedName.includes(keyword)) {
      return category
    }
  }

  return null
}

/**
 * Determines the food category for a given food name using enhanced multi-strategy parsing.
 *
 * @param foodName - The name of the food item to categorize
 * @returns The determined food category, or 'Unknown' if no match is found
 *
 * @example
 * ```typescript
 * categorizeFood('Apple') // Returns: 'Fruits and Vegetables'
 * categorizeFood('Almonds') // Returns: 'Nuts and Seeds'
 * categorizeFood('Hazelnuts (Sources-usda) - Hazelnuts') // Returns: 'Nuts and Seeds'
 * categorizeFood('Unknown Food') // Returns: 'Unknown'
 * ```
 */
export function categorizeFood(foodName: string): FoodCategory {
  const result = categorizationEngine.categorize(foodName)
  return result.category
}

/**
 * Enhanced categorization function that returns detailed analysis results.
 * Useful for debugging and understanding categorization decisions.
 *
 * @param foodName - The name of the food item to categorize
 * @returns Detailed categorization result with strategy and confidence
 *
 * @example
 * ```typescript
 * const result = categorizeFoodDetailed('Almonds')
 * console.log(result.category) // 'Nuts and Seeds'
 * console.log(result.strategy) // 'exact'
 * console.log(result.confidence) // 1.0
 * ```
 */
export function categorizeFoodDetailed(foodName: string): CategorizationResult {
  return categorizationEngine.categorize(foodName)
}

/**
 * Gets all available food categories.
 *
 * @returns Array of all available food categories
 */
export function getAllFoodCategories(): readonly FoodCategory[] {
  return Object.freeze([
    'Fruits and Vegetables',
    'Legumes',
    'Whole Grains',
    'Nuts and Seeds',
    'Root Vegetables',
    'Herbs and Spices',
    'Meat, Poultry, Fish & Seafood',
    'Dairy & Eggs',
    'Fats, Oils & Spreads',
    'Processed Foods & Snacks',
    'Sweets, Sugars & Desserts',
    'Supplements & Protein Powders',
    'Beverages',
    'Unknown',
  ] as const)
}
